import { Injectable } from '@angular/core';
import { RecoveryOptionsContent } from '@com-delta-idp/mnx-login-ui';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { LoginHelperUtils } from '../../../utils/login-helper.utils';
import { RecoveryOptionsAdapter } from '../../adapters/recovery-options/recovery-options.adapters';
import { ResetPasswordAdapter } from '../../adapters/reset-password/reset-password.adapters';
import { LOGIN_CONSTANTS } from '../../constants/login-constant';
import { LoginModalContent } from '../../models/aem';
import {
  ResetPasswordEmailLinkResponse,
  ResetPasswordUserResponse,
  error
} from '../../models/reset-password-api.interface';
import {
  LoginContentService,
  NextDisplayViewService,
  ResetPasswordApiService,
  ResetPasswordHelperService
} from '../../services';
import { CheckEmailFacadeService } from '../check-email-facade/check-email-facade.service';
import { RecoverSQAFacadeService } from '../recover-sqa-facade/recover-sqa-facade.service';

@Injectable({
  providedIn: 'root'
})
export class RecoveryOptionsFacade {
  aemContent!: LoginModalContent;
  recoveryOptionsContent$: BehaviorSubject<RecoveryOptionsContent | null> =
    new BehaviorSubject<RecoveryOptionsContent | null>(null);

  unsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private resetPasswordAdapter: ResetPasswordAdapter,
    private resetPasswordhelperService: ResetPasswordHelperService,
    protected loginHelperUtils: LoginHelperUtils,
    private contentService: LoginContentService,
    private resetPasswordApiService: ResetPasswordApiService,
    private recoveryOptionsAdapter: RecoveryOptionsAdapter,
    private checkEmailFacadeService: CheckEmailFacadeService,
    private nextDisplayViewService: NextDisplayViewService,
    private recoverSQAFacadeService: RecoverSQAFacadeService
  ) {}

  getContent(): RecoveryOptionsContent {
    return this.recoveryOptionsContent$.value || ({} as RecoveryOptionsContent);
  }

  setContent(
    aemContent: LoginModalContent,
    bffResponse: ResetPasswordUserResponse
  ) {
    this.aemContent = aemContent;
    const content = this.recoveryOptionsAdapter.geRecoveryOptionsContent(
      aemContent,
      bffResponse
    );
    this.recoveryOptionsContent$.next(content);
  }

  onFormSubmit() {
    const formGroup = this.getContent().formGroup;
    if (formGroup.invalid) {
      return;
    }
    const selectedOption = formGroup.controls.options.value;
    this.getContent().disabledActionButton = true;
    if (selectedOption.value === LOGIN_CONSTANTS.RECOVERY_OPTION_EMAIL) {
      this.sendEmailLink();
    }
    if (selectedOption.value === LOGIN_CONSTANTS.RECOVERY_OPTION_SQA) {
      this.recoverSQAFacadeService.getSQADetails();
      this.recoverSQAFacadeService.sqaError$.subscribe((response) => {
        if (response) {
          this.getContent().errorMessage = response;
          this.showHideOverlayMessage(true);
        }
      });
    }
  }

  sendEmailLink(emailMessage?: string | undefined, nextViewName = '') {
    this.resetPasswordApiService
      .sendResetPasswordEmailLink()
      .pipe(
        takeUntil(this.unsubscribe),
        finalize(() => (this.getContent().disabledActionButton = false))
      )
      .subscribe(
        (response) => {
          this.getContent().disabledActionButton = true;
          if (response?.errors && response.errors.length > 0) {
            this.handleError(response?.errors);
          } else if (response?.isSuccess) {
            this.sendEmailResponseHandler(response, emailMessage, nextViewName);
          }
        },
        (err) => this.handleError(err?.error?.errors)
      );
  }

  sendEmailResponseHandler(
    response: ResetPasswordEmailLinkResponse,
    emailMessage: string | undefined,
    nextViewName = ''
  ) {
    if (!emailMessage) {
      const { checkEmailMessage_Password } =
        this.aemContent && this.aemContent.commonOverlayMessages;
      const { nextViewName_email } =
        this.getContent().pageContent.nextDisplayView;
      this.checkEmailFacadeService.getContent().checkEmailMessage =
        this.loginHelperUtils.maskEmail(
          checkEmailMessage_Password,
          response.email
        );
      this.nextDisplayViewService.nextDisplayView(nextViewName_email);
    } else {
      this.checkEmailFacadeService.getContent().checkEmailMessage =
        emailMessage;
      this.nextDisplayViewService.nextDisplayView(nextViewName);
    }
  }

  handleError(errors: error[]) {
    this.getContent().errorMessage =
      this.loginHelperUtils.getErrorMessageFromAem(
        this.aemContent,
        `ERROR_MSG_${errors[0]?.code}`
      );
    this.showHideOverlayMessage(true);
  }

  showHideOverlayMessage(value: boolean) {
    this.getContent().hasError = value;
  }
}
